import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { updateChannelSubscribers } from '../data/channels';
import ChannelCard from '../components/ChannelCard';
import StructuredData from '../components/StructuredData';
import { useYouTubeStats } from '../hooks/useYouTubeStats';

const HomePage = () => {
  const { channelStats, loading } = useYouTubeStats();
  
  // Update channels with real API data
  const updatedChannels = updateChannelSubscribers(channelStats);
  
  // Calculate total stats
  const totalSubscribers = Object.values(channelStats).reduce((sum, stats) => sum + (stats.subscriberCount || 0), 0);
  const totalVideos = Object.values(channelStats).reduce((sum, stats) => sum + (stats.videoCount || 0), 0);
  const totalViews = Object.values(channelStats).reduce((sum, stats) => sum + (stats.viewCount || 0), 0);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M+`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K+`;
    } else {
      return `${num}+`;
    }
  };

  return (
    <>
      <StructuredData type="website" />
      <StructuredData type="organization" />
      <Helmet>
        <title>VR Media Network – Creating Stories. Sharing Moments.</title>
        <meta name="description" content="VR Media Network manages YouTube channels across entertainment, lifestyle, and travel, creating content that inspires, educates, and entertains." />
        <meta name="keywords" content="VR Media, VR Media Network, entertainment YouTube, travel vlogs India, cinema updates, lifestyle content, YouTube channels India" />
        <link rel="canonical" href="https://vr-media.in/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="VR Media Network – Creating Stories. Sharing Moments." />
        <meta property="og:description" content="Explore our network of YouTube channels across entertainment, lifestyle, cinema, travel vlogs, and more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vr-media.in/" />
        <meta property="og:image" content="https://vr-media.in/og-image.jpg" />
        <meta property="og:site_name" content="VR Media Network" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="VR Media Network – Creating Stories. Sharing Moments." />
        <meta name="twitter:description" content="VR Media creates engaging YouTube content across cinema, travel, lifestyle, and more." />
        <meta name="twitter:image" content="https://vr-media.in/twitter-image.jpg" />
        <meta name="twitter:site" content="@VRMediaIndia" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="VR Media Network" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content="India" />
        
        {/* Schema.org JSON-LD for Homepage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "VR Media Network",
            "url": "https://vr-media.in",
            "description": "VR Media Network manages YouTube channels delivering engaging content in cinema, travel, lifestyle, and entertainment.",
            "publisher": {
              "@type": "Organization",
              "name": "VR Media Network",
              "logo": {
                "@type": "ImageObject",
                "url": "https://vr-media.in/logo.png"
              }
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://vr-media.in/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <motion.section 
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-vr-dark via-vr-darker to-vr-dark overflow-hidden"
      >
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{ 
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-20 left-10 w-72 h-72 bg-vr-red/10 rounded-full blur-3xl"
          />
          <motion.div 
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.1, 0.15, 0.1]
            }}
            transition={{ 
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
            className="absolute bottom-20 right-10 w-96 h-96 bg-vr-blue/10 rounded-full blur-3xl"
          />
          <motion.div 
            animate={{ 
              scale: [1, 1.1, 1],
              opacity: [0.1, 0.18, 0.1]
            }}
            transition={{ 
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-vr-pink/10 rounded-full blur-3xl"
          />
        </div>

        <div className="relative z-10 text-center px-4">
          <div className="mb-8">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-7xl font-bold font-display text-white mb-6"
            >
              VR Media Network
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-2xl md:text-3xl text-gray-300 mb-8"
            >
              Creating Stories. Sharing Moments.
            </motion.p>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-lg text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed"
            >
              VR Media Network is a growing digital media brand managing YouTube channels in India. From cinema updates and celebrity interviews to immersive travel vlogs and lifestyle features, we create content that inspires, educates, and entertains audiences worldwide.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link to="/channels" className="btn-primary inline-flex items-center space-x-2">
                  <span>Explore Our Channels</span>
                  <ArrowRight size={20} />
                </Link>
              </motion.div>
              <motion.button 
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary inline-flex items-center space-x-2"
              >
                <Play size={20} />
                <span>Watch Our Story</span>
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <motion.div 
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 bg-white rounded-full mt-2"
            />
          </div>
        </motion.div>
      </motion.section>

      {/* Channel Showcase Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-16 bg-vr-darker"
      >
        <div className="container-custom">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-4">
              Entertainment & Lifestyle Channels
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Explore our comprehensive collection of entertainment channels featuring cinema updates, 
              celebrity interviews, travel vlogs, and lifestyle trends. Your one-stop destination for 
              entertainment news and public opinions in India.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {updatedChannels.map((channel, index) => (
              <motion.div
                key={channel.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ChannelCard channel={channel} />
              </motion.div>
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link to="/channels" className="btn-secondary inline-flex items-center space-x-2">
                <span>View All Channels</span>
                <ArrowRight size={20} />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>



      {/* Stats Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="section-padding bg-vr-dark"
      >
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: '5', label: 'YouTube Channels' },
              { number: loading ? '...' : formatNumber(totalSubscribers), label: 'Total Subscribers' },
              { number: loading ? '...' : formatNumber(totalVideos), label: 'Videos Created' },
              { number: loading ? '...' : formatNumber(totalViews), label: 'Views Generated' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <motion.div 
                  className="text-3xl md:text-4xl font-bold text-white mb-2"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.3, type: "spring", stiffness: 200 }}
                  viewport={{ once: true }}
                >
                  {stat.number}
                </motion.div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
    </>
  );
};

export default HomePage;
